import { Form } from '@form/Form';
import FormInput from '@form/FormInput';
import Joi from 'joi';
import moment from 'moment';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSWRConfig } from 'swr';
import { useToastStore } from '~/components/ToastManager/useToasts';
import { Blockquote, FormRow } from '~/components/ui';
import { useApi } from '../../../hooks/useApi';
import { memberFilesKey } from '../ClubMemberFiles';
import { ClubMember } from '../types';

interface Props {
    member: ClubMember;
    /** receives the refreshed member row returned by the API */
    onSuccess?: (member: ClubMember) => void;
    callback?: () => void;
}

/**
 * Records a cancellation with exit date, receipt date and reason. The backend ends the
 * member's lockers on the exit date, so the form only has to say which ones.
 */
export const CancelClubMemberForm = ({ member, onSuccess, callback }: Props) => {
    const { t } = useTranslation('member-management');
    const api = useApi();
    const { addToast } = useToastStore();
    const [isLoading, setIsLoading] = useState(false);
    const [hasPendingFiles, setHasPendingFiles] = useState(false);
    const { mutate } = useSWRConfig();

    const defaultValues = useMemo(
        () => ({
            exit_date: member.exit_date ? moment(member.exit_date).toDate() : null,
            cancellation_received_on: moment(member.cancellation_received_on ?? undefined).toDate(),
            reason: member.cancellation_reason ?? '',
            send_confirmation: !!member.email
        }),
        [member.exit_date, member.cancellation_received_on, member.cancellation_reason, member.email]
    );

    const lockerNumbers = member.lockers?.map((locker) => locker.number).join(', ');

    const handleSubmit = (values: any) => {
        if (hasPendingFiles) return;

        setIsLoading(true);
        return api({
            url: `club-member/${member.id}/exit`,
            method: 'PUT',
            data: {
                exit_date: values.exit_date ? moment(values.exit_date).format('YYYY-MM-DD') : undefined,
                cancellation_received_on: values.cancellation_received_on
                    ? moment(values.cancellation_received_on).format('YYYY-MM-DD')
                    : undefined,
                reason: values.reason || undefined,
                send_confirmation: !!values.send_confirmation,
                message: values.send_confirmation && values.message ? values.message : undefined,
                files: values.files?.length ? values.files : undefined
            },
            onBefore: () => setIsLoading(false),
            onSuccess: ({ data, skipped_lockers }) => {
                if (skipped_lockers?.length) {
                    addToast({
                        type: 'alert',
                        title: t('clubMember.exit.skippedLockers', { numbers: skipped_lockers.join(', ') })
                    });
                }
                // opened from the list or the preview, the file tab must not keep its cached list
                if (values.files?.length) void mutate(memberFilesKey(member.id));
                onSuccess?.(data);
                callback?.();
            }
        });
    };

    return (
        <>
            <Blockquote>{t('clubMember.exit.form.hint')}</Blockquote>
            {lockerNumbers && (
                <Blockquote color="warning" spacing>
                    {t('clubMember.exit.form.lockers', { numbers: lockerNumbers })}
                </Blockquote>
            )}
            <Form
                onSubmit={handleSubmit}
                defaultValues={defaultValues}
                validationSchema={Joi.object({
                    exit_date: Joi.date().required().label(t('clubMember.exit.form.exitDate')),
                    cancellation_received_on: Joi.date().required().label(t('clubMember.exit.form.receivedOn')),
                    reason: Joi.string().allow('', null).max(2000).label(t('clubMember.exit.form.reason')),
                    send_confirmation: Joi.boolean(),
                    message: Joi.string().allow('', null).max(5000).label(t('clubMember.exit.form.message')),
                    files: Joi.array().optional()
                })}
            >
                <FormRow>
                    <FormInput.DatePicker
                        name="exit_date"
                        label={t('clubMember.exit.form.exitDate')}
                        helperText={t('clubMember.exit.form.exitDateHelp')}
                    />
                    <FormInput.DatePicker
                        name="cancellation_received_on"
                        label={t('clubMember.exit.form.receivedOn')}
                        helperText={t('clubMember.exit.form.receivedOnHelp')}
                    />
                </FormRow>
                <FormInput.Textarea name="reason" label={t('clubMember.exit.form.reason')} />
                <div className="tw-mb-1 tw-text-sm tw-font-semibold">{t('clubMember.exit.form.files')}</div>
                <p className="tw-mb-2 tw-mt-0 tw-text-xs tw-text-accents-6">{t('clubMember.exit.form.filesHelp')}</p>
                {/* FormDropzone renders no label of its own */}
                <FormInput.Files
                    name="files"
                    valueMapping={(id) => ({ id })}
                    dropzone={{ accept: ['application/pdf', 'image/*'] }}
                    onPendingChange={setHasPendingFiles}
                />
                {hasPendingFiles && (
                    <Blockquote color="warning" size="sm" spacing>
                        {t('clubMember.exit.form.filesUploading')}
                    </Blockquote>
                )}
                <FormInput.Checkbox
                    name="send_confirmation"
                    label={t('clubMember.exit.form.sendConfirmation')}
                    isDisabled={!member.email}
                />
                {member.email ? (
                    <FormInput.Textarea
                        name="message"
                        label={t('clubMember.exit.form.message')}
                        helperText={t('clubMember.exit.form.messageHelp')}
                    />
                ) : (
                    <p className="tw-text-xs tw-text-accents-6">{t('clubMember.exit.form.noEmail')}</p>
                )}
                <FormInput.Submit isLoading={isLoading} disabled={hasPendingFiles} color="error">
                    {t('clubMember.exit.form.submit')}
                </FormInput.Submit>
            </Form>
        </>
    );
};

export default CancelClubMemberForm;
